import React, { useState } from 'react';
import { usePolicy } from '../context/PolicyContext';
import './Form.css';

const CreatePolicy: React.FC = () => {
  const { addPolicy } = usePolicy();
  const [formData, setFormData] = useState({
    policyHolderName: '',
    policyHolderEmail: '',
    policyHolderPhone: '',
    policyType: '',
    coverageAmount: '',
    premiumAmount: '',
    startDate: '',
    endDate: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.policyHolderName.trim()) {
      newErrors.policyHolderName = 'Policy holder name is required';
    }

    if (!formData.policyHolderEmail.trim()) {
      newErrors.policyHolderEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.policyHolderEmail)) {
      newErrors.policyHolderEmail = 'Invalid email format';
    }

    if (!formData.policyHolderPhone.trim()) {
      newErrors.policyHolderPhone = 'Phone number is required';
    }

    if (!formData.policyType) {
      newErrors.policyType = 'Policy type is required';
    }

    if (!formData.coverageAmount || parseFloat(formData.coverageAmount) <= 0) {
      newErrors.coverageAmount = 'Coverage amount must be greater than 0';
    }

    if (!formData.premiumAmount || parseFloat(formData.premiumAmount) <= 0) {
      newErrors.premiumAmount = 'Premium amount must be greater than 0';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }

    if (formData.startDate && formData.endDate && new Date(formData.startDate) >= new Date(formData.endDate)) {
      newErrors.endDate = 'End date must be after start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    const policyNumber = `POL-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
    
    const newPolicy = {
      id: Date.now().toString(),
      policyNumber,
      policyHolderName: formData.policyHolderName,
      policyHolderEmail: formData.policyHolderEmail,
      policyHolderPhone: formData.policyHolderPhone,
      policyType: formData.policyType,
      coverageAmount: parseFloat(formData.coverageAmount),
      premiumAmount: parseFloat(formData.premiumAmount),
      startDate: formData.startDate,
      endDate: formData.endDate,
      status: 'active' as const,
      incidents: [],
    };

    addPolicy(newPolicy);
    setSuccess(true);
    setFormData({
      policyHolderName: '',
      policyHolderEmail: '',
      policyHolderPhone: '',
      policyType: '',
      coverageAmount: '',
      premiumAmount: '',
      startDate: '',
      endDate: '',
    });

    setTimeout(() => setSuccess(false), 5000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className="form-container">
      <h2>Create New Insurance Policy</h2>
      
      {success && (
        <div className="success-message">
          Policy created successfully! You can now retrieve it using the policy number.
        </div>
      )}

      <form onSubmit={handleSubmit} className="form">
        <div className="form-group">
          <label htmlFor="policyHolderName">Policy Holder Name *</label>
          <input
            type="text"
            id="policyHolderName"
            name="policyHolderName"
            value={formData.policyHolderName}
            onChange={handleChange}
            className={errors.policyHolderName ? 'error' : ''}
          />
          {errors.policyHolderName && <span className="error-text">{errors.policyHolderName}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="policyHolderEmail">Email *</label>
          <input
            type="email"
            id="policyHolderEmail"
            name="policyHolderEmail"
            value={formData.policyHolderEmail}
            onChange={handleChange}
            className={errors.policyHolderEmail ? 'error' : ''}
          />
          {errors.policyHolderEmail && <span className="error-text">{errors.policyHolderEmail}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="policyHolderPhone">Phone Number *</label>
          <input
            type="tel"
            id="policyHolderPhone"
            name="policyHolderPhone"
            value={formData.policyHolderPhone}
            onChange={handleChange}
            className={errors.policyHolderPhone ? 'error' : ''}
          />
          {errors.policyHolderPhone && <span className="error-text">{errors.policyHolderPhone}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="policyType">Policy Type *</label>
          <select
            id="policyType"
            name="policyType"
            value={formData.policyType}
            onChange={handleChange}
            className={errors.policyType ? 'error' : ''}
          >
            <option value="">Select Policy Type</option>
            <option value="health">Health Insurance</option>
            <option value="life">Life Insurance</option>
            <option value="auto">Auto Insurance</option>
            <option value="home">Home Insurance</option>
            <option value="travel">Travel Insurance</option>
          </select>
          {errors.policyType && <span className="error-text">{errors.policyType}</span>}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="coverageAmount">Coverage Amount ($) *</label>
            <input
              type="number"
              id="coverageAmount"
              name="coverageAmount"
              value={formData.coverageAmount}
              onChange={handleChange}
              min="0"
              step="0.01"
              className={errors.coverageAmount ? 'error' : ''}
            />
            {errors.coverageAmount && <span className="error-text">{errors.coverageAmount}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="premiumAmount">Premium Amount ($) *</label>
            <input
              type="number"
              id="premiumAmount"
              name="premiumAmount"
              value={formData.premiumAmount}
              onChange={handleChange}
              min="0"
              step="0.01"
              className={errors.premiumAmount ? 'error' : ''}
            />
            {errors.premiumAmount && <span className="error-text">{errors.premiumAmount}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="startDate">Start Date *</label>
            <input
              type="date"
              id="startDate"
              name="startDate"
              value={formData.startDate}
              onChange={handleChange}
              className={errors.startDate ? 'error' : ''}
            />
            {errors.startDate && <span className="error-text">{errors.startDate}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="endDate">End Date *</label>
            <input
              type="date"
              id="endDate"
              name="endDate"
              value={formData.endDate}
              onChange={handleChange}
              min={formData.startDate}
              className={errors.endDate ? 'error' : ''}
            />
            {errors.endDate && <span className="error-text">{errors.endDate}</span>}
          </div>
        </div>

        <button type="submit" className="submit-button">Create Policy</button>
      </form>
    </div>
  );
};

export default CreatePolicy;


